import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { User, Mail, Building2, Bell, Lock, CreditCard, Loader2, Trash2, ShieldAlert } from "lucide-react";

function Card({ icon: Icon, title, desc, children, tone = "primary", testid }) {
  const tones = {
    primary: "border-primary/25 bg-primary/10 text-primary",
    amber: "border-amber-500/25 bg-amber-500/10 text-amber-400",
    rose: "border-rose-500/25 bg-rose-500/10 text-rose-400",
  };
  return (
    <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-6" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`h-9 w-9 rounded-lg border flex items-center justify-center ${tones[tone]}`}><Icon size={17} /></div>
        <div>
          <div className="font-serif font-semibold text-lg">{title}</div>
          {desc && <div className="text-xs text-muted-foreground">{desc}</div>}
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

export default function Account() {
  const { user, refresh, setUser } = useAuth();
  const nav = useNavigate();

  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [company, setCompany] = useState("");
  const [emailNotif, setEmailNotif] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [delPw, setDelPw] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setContactEmail(user.contact_email || "");
      setCompany(user.company || "");
      setEmailNotif(user.email_notifications !== false);
    }
  }, [user]);

  const isEmailUser = (user?.auth_provider || "email") === "email";
  const plan = user?.plan || "free";

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.post("/account/profile", { name, contact_email: contactEmail, company, email_notifications: emailNotif });
      await refresh();
      toast.success("Account details saved.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async () => {
    if (newPw.length < 6) { toast.error("New password must be at least 6 characters."); return; }
    setSavingPw(true);
    try {
      await api.post("/account/change-password", { current_password: curPw, new_password: newPw });
      setCurPw(""); setNewPw("");
      toast.success("Password updated.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingPw(false);
    }
  };

  const manageBilling = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal");
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPortalBusy(false);
    }
  };

  const deleteAccount = async () => {
    if (isEmailUser && !delPw) { toast.error("Enter your password to confirm."); return; }
    setDeleting(true);
    try {
      await api.post("/account/delete", { password: delPw });
      setUser(false);
      toast.success("Your account has been deleted.");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="account-page">
      <div className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground uppercase">Settings</div>
      <h1 className="font-serif font-semibold text-3xl sm:text-4xl tracking-tight mt-1">Your account</h1>

      <div className="mt-8 space-y-5">
        {/* Profile */}
        <Card icon={User} title="Profile & contact" desc="How we address you and reach you." testid="account-profile-card">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Display name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="account-name-input"
                className="rounded-lg bg-zinc-950 border-white/10 h-11 focus-visible:ring-primary" placeholder="Jane Doe" />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Mail size={13} /> Login email</Label>
              <Input value={user?.email || ""} disabled data-testid="account-login-email"
                className="rounded-lg bg-zinc-950/60 border-white/10 h-11 text-muted-foreground" />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Mail size={13} /> Contact email</Label>
              <Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} data-testid="account-contact-email-input"
                className="rounded-lg bg-zinc-950 border-white/10 h-11 focus-visible:ring-primary" placeholder="you@company.com" />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Building2 size={13} /> Company / brand</Label>
              <Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="account-company-input"
                className="rounded-lg bg-zinc-950 border-white/10 h-11 focus-visible:ring-primary" placeholder="Optional" />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between rounded-lg border border-white/10 bg-zinc-950/50 px-4 py-3">
            <div className="flex items-center gap-2.5">
              <Bell size={16} className="text-primary" />
              <div>
                <div className="text-sm font-medium">Email me about new matches</div>
                <div className="text-[11px] text-muted-foreground">We'll email you when a new match lands (coming soon).</div>
              </div>
            </div>
            <button onClick={() => setEmailNotif((v) => !v)} data-testid="account-email-notifications-toggle"
              role="switch" aria-checked={emailNotif}
              className={`relative h-6 w-11 rounded-full transition-colors ${emailNotif ? "bg-primary" : "bg-white/15"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${emailNotif ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>

          <Button onClick={saveProfile} disabled={savingProfile} data-testid="account-save-profile"
            className="mt-5 rounded-full bg-primary text-white hover:bg-blue-600 px-6 active:scale-95 transition-transform">
            {savingProfile ? <Loader2 size={15} className="animate-spin mr-2" /> : null} Save changes
          </Button>
        </Card>

        {/* Security */}
        <Card icon={Lock} title="Password" desc="Keep your account secure." testid="account-security-card">
          {isEmailUser ? (
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Current password</Label>
                <Input type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} data-testid="account-current-password"
                  className="rounded-lg bg-zinc-950 border-white/10 h-11 focus-visible:ring-primary" placeholder="••••••••" />
              </div>
              <div className="space-y-1.5">
                <Label>New password</Label>
                <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="account-new-password"
                  className="rounded-lg bg-zinc-950 border-white/10 h-11 focus-visible:ring-primary" placeholder="min 6 characters" />
              </div>
              <div className="sm:col-span-2">
                <Button onClick={changePassword} disabled={savingPw || !curPw || !newPw} data-testid="account-change-password-btn"
                  className="rounded-full bg-white/10 text-foreground hover:bg-white/15 px-6 active:scale-95 transition-transform">
                  {savingPw ? <Loader2 size={15} className="animate-spin mr-2" /> : null} Update password
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">You sign in with Google, so there's no password to manage here.</p>
          )}
        </Card>

        {/* Billing */}
        <Card icon={CreditCard} title="Plan & billing" desc="Manage your subscription." testid="account-billing-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm">
              Current plan: <span className="font-mono font-semibold uppercase text-foreground" data-testid="account-plan">{plan}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => nav("/pricing")} data-testid="account-upgrade"
                className="rounded-full bg-primary text-white hover:bg-blue-600 px-5 active:scale-95 transition-transform">
                {plan === "premium" ? "View plans" : "Upgrade"}
              </Button>
              {plan !== "free" && (
                <Button onClick={manageBilling} disabled={portalBusy} variant="outline" data-testid="account-manage-billing"
                  className="rounded-full border-white/15 bg-white/5 hover:bg-white/10 px-5">
                  {portalBusy ? <Loader2 size={15} className="animate-spin mr-2" /> : null} Manage subscription
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Danger zone */}
        <Card icon={ShieldAlert} title="Delete account" desc="Permanently remove your account and data." tone="rose" testid="account-danger-card">
          {!confirmDelete ? (
            <Button onClick={() => setConfirmDelete(true)} data-testid="account-delete-btn"
              className="rounded-full bg-rose-500/15 text-rose-400 border border-rose-500/30 hover:bg-rose-500/25 px-5">
              <Trash2 size={15} className="mr-2" /> Delete my account
            </Button>
          ) : (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/[0.06] p-4">
              <p className="text-sm text-rose-200/90">This deletes your profile, site, matches and swipes. This can't be undone.</p>
              {isEmailUser && (
                <Input type="password" value={delPw} onChange={(e) => setDelPw(e.target.value)} data-testid="account-delete-password"
                  className="mt-3 rounded-lg bg-zinc-950 border-white/10 h-11 focus-visible:ring-rose-500 max-w-xs" placeholder="Confirm with your password" />
              )}
              <div className="mt-4 flex items-center gap-2">
                <Button onClick={deleteAccount} disabled={deleting} data-testid="account-delete-confirm"
                  className="rounded-full bg-rose-500 text-white hover:bg-rose-600 px-5 active:scale-95 transition-transform">
                  {deleting ? <Loader2 size={15} className="animate-spin mr-2" /> : null} Yes, delete permanently
                </Button>
                <Button onClick={() => { setConfirmDelete(false); setDelPw(""); }} variant="outline" data-testid="account-delete-cancel"
                  className="rounded-full border-white/15 bg-white/5 hover:bg-white/10 px-5">Cancel</Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
